import {BankAccount} from "../../domain/bank-account.entity";
import {
  BankAccountStatus,
  BankAccountProvider,
  BANK_BRANCH_COUNTRIES,
  PayoutCountry,
  PayoutBank,
  BankBranch,
  BankValidationResult,
  ValidateBankParams,
  ConnectBankParams,
  UpdateBankParams,
} from "../../domain/bank-account.types";
import {
  V2BankResponse,
  V2ConnectedBankResponse,
  V2PayoutCountriesResponse,
  V2PayoutBanksResponse,
  V2BankBranchesResponse,
  V2ValidateAccountResponse,
  V2ValidateBankRequest,
  V2ConnectBankRequest,
  V2InitiateUpdateRequest,
  V2CompleteUpdateRequest,
} from "./bank-account.mapper.types";

/**
 * Maps v2 API responses to BankAccount domain entities.
 *
 * Key differences from v1:
 * - GET /payments/bank returns bank directly in data (not nested under 'bank' key)
 * - Banks response is array directly in data
 * - Branches use snake_case field names
 * - Update flow is two-step: initiate (sends OTP) then complete (with otpCode)
 *
 * Pure TypeScript - no React imports.
 */
export const BankAccountMapperV2 = {
  /**
   * Maps a v2 bank response to a BankAccount entity.
   * Handles optional fields gracefully with defaults.
   */
  toDomain(response: V2BankResponse): BankAccount {
    return BankAccount.create({
      id: response.id ?? 0,
      userId: response.userId,
      countryName: response.countryName ?? "",
      countryCode: response.countryCode ?? "",
      currency: response.currency,
      bankName: response.bankName,
      bankCode: response.bankCode ?? "",
      accountNumber: response.accountNumber,
      accountName: response.accountName,
      statusId: response.statusId ?? "",
      status: this.mapStatus(response.statusName),
      branchCode: response.branchCode ?? null,
      provider: this.mapProvider(response.provider),
      createdAt: response.createdAt,
      updatedAt: response.updatedAt ?? response.createdAt,
      loginUrl: response.loginUrl ?? null,
    });
  },

  /**
   * Unwraps data envelope from GET /payments/bank and maps to BankAccount.
   */
  fromConnectedBankResponse(response: V2ConnectedBankResponse): BankAccount {
    return this.toDomain(response.data);
  },

  /**
   * Maps v2 payout countries response to domain array.
   * v2 returns array directly in data (not under 'countries' key).
   */
  toCountriesList(response: V2PayoutCountriesResponse): PayoutCountry[] {
    return response.data.map((country) => ({
      name: country.name,
      code: country.code,
      currency: country.currency,
    }));
  },

  /**
   * Maps v2 payout banks response to domain array.
   * Extracts only the fields needed for PayoutBank.
   */
  toBanksList(response: V2PayoutBanksResponse, countryCode: string): PayoutBank[] {
    const isBranchCountry = BANK_BRANCH_COUNTRIES.includes(countryCode);
    return response.data.map((bank) => ({
      id: bank.id,
      code: bank.code,
      name: bank.name,
      hasBankBranch: isBranchCountry,
    }));
  },

  /**
   * Maps v2 bank branches response to domain array.
   * Note: v2 uses snake_case field names.
   */
  toBranchesList(response: V2BankBranchesResponse): BankBranch[] {
    return response.data.map((branch) => ({
      id: branch.id,
      branchCode: branch.branch_code,
      branchName: branch.branch_name,
      swiftCode: branch.swift_code,
      bic: branch.bic,
      bankId: branch.bank_id,
    }));
  },

  /**
   * Maps v2 validation response to BankValidationResult.
   * Note: v2 returns account name directly as data string.
   */
  toValidationResult(response: V2ValidateAccountResponse): BankValidationResult {
    return {
      accountName: response.data,
      isValid: Boolean(response.data),
    };
  },

  /**
   * Maps ValidateBankParams to v2 API request.
   * v2 doesn't use bankName in validation request.
   */
  toValidateBankRequest(params: ValidateBankParams): V2ValidateBankRequest {
    return {
      accountNumber: params.accountNumber ?? "",
      bankCode: params.bankCode,
      countryCode: params.countryCode,
    };
  },

  /**
   * Maps ConnectBankParams to v2 API request.
   * Excludes routingNumber (not used in v2).
   */
  toConnectBankRequest(params: ConnectBankParams): V2ConnectBankRequest {
    const request: V2ConnectBankRequest = {
      accountNumber: params.accountNumber,
      bankCode: params.bankCode,
      bankName: params.bankName,
      countryCode: params.countryCode,
    };

    if (params.branchCode) {
      request.branchCode = params.branchCode;
    }

    return request;
  },

  /**
   * Maps UpdateBankParams to v2 initiate update request.
   * This is the first step - sends OTP to user.
   * OTP is NOT included in this request.
   */
  toInitiateUpdateRequest(params: UpdateBankParams): V2InitiateUpdateRequest {
    const request: V2InitiateUpdateRequest = {
      accountNumber: params.accountNumber,
      bankCode: params.bankCode,
      bankName: params.bankName,
      countryCode: params.countryCode,
    };

    if (params.branchCode) {
      request.branchCode = params.branchCode;
    }

    return request;
  },

  /**
   * Creates v2 complete update request with OTP.
   * This is the second step - completes the update.
   */
  toCompleteUpdateRequest(otp: string): V2CompleteUpdateRequest {
    return {
      otpCode: otp,
    };
  },

  /**
   * Maps API status string to domain BankAccountStatus.
   */
  mapStatus(statusName: string): BankAccountStatus {
    switch (statusName) {
      case "Active":
        return "Active";
      case "Pending":
        return "Pending";
      case "Inactive":
        return "Inactive";
      default:
        return "Active";
    }
  },

  /**
   * Maps API provider string to domain BankAccountProvider.
   */
  mapProvider(provider: string): BankAccountProvider {
    switch (provider) {
      case "paystack":
        return "paystack";
      case "flutterwave":
        return "flutterwave";
      case "stripe":
        return "stripe";
      default:
        return "paystack";
    }
  },
};
